// ─────────────────────────────────────────────────────────────────────────────
// authService.js — All authentication API calls
//
// Enterprise backend response shape (login):
//   {
//     access_token:  string,   ← snake_case
//     refresh_token: string,   ← snake_case
//     user: { id, email, firstName, lastName, role, companyId }
//   }
//
// MFA flow (login returns this instead of tokens):
//   { mfa_required: true, temp_token: string }
//   → POST /auth/mfa/verify { temp_token, code } → same shape as login success
//   → POST /auth/mfa/resend { temp_token } → { mfa_required, temp_token } (new code)
// ─────────────────────────────────────────────────────────────────────────────
import api, { TokenStorage } from "./api";

// ── Internal helper ───────────────────────────────────────────────────────────
// Accepts both snake_case (enterprise backend) and camelCase (fallback) so a
// backend field-name difference never silently drops the token. The refresh
// token is NOT handled here — it arrives as an httpOnly cookie the backend sets.
function storeTokens(data) {
  const accessToken = data?.access_token ?? data?.accessToken;
  if (accessToken) TokenStorage.set(accessToken);
  if (data?.user)  TokenStorage.setUser(data.user);
}

const authService = {

  // POST /auth/login
  // body:     { email, password }
  // response: { access_token, refresh_token, user } on success
  //        OR { mfa_required: true, temp_token } when MFA is enabled
  login: async ({ email, password }) => {
    const data = await api.post("/auth/login", { email, password });
    if (data?.mfa_required) return data; // caller handles MFA flow
    storeTokens(data);
    return data;
  },

  // POST /auth/mfa/verify
  // body:     { temp_token, code }
  // response: { access_token, refresh_token, user }
  verifyMfa: async ({ temp_token, code }) => {
    const data = await api.post("/auth/mfa/verify", { temp_token, code });
    storeTokens(data);
    return data;
  },

  // POST /auth/mfa/resend
  // Re-emails a fresh code for an in-flight MFA challenge.
  // body:     { temp_token }
  // response: { mfa_required: true, temp_token }  ← new temp_token (5-min window resets)
  resendMfa: async ({ temp_token }) => {
    return api.post("/auth/mfa/resend", { temp_token });
  },

  // POST /auth/refresh
  // The refresh token rides an httpOnly cookie (no body). The rotated refresh
  // token comes back as a fresh cookie; only the access token is in the response.
  // response: { access_token }
  refresh: async () => {
    const data = await api.post("/auth/refresh");
    if (data?.access_token) TokenStorage.set(data.access_token);
    return data;
  },

  // POST /auth/logout
  logout: async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    TokenStorage.clear();
  },

  // POST /auth/forgot-password
  // body: { email }
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),

  // POST /auth/reset-password
  // body: { token, password }
  resetPassword: (token, password) =>
    api.post("/auth/reset-password", { token, password }),

  // GET /auth/me — returns full enterprise user object (requires valid token)
  me: () => api.get("/auth/me"),

  // POST /auth/desktop-handoff — mint a single-use code that signs THIS user
  // into the desktop app. Lives 60 seconds; minting again invalidates the
  // previous one. Response: { code, expires_at, deep_link }
  // The desktop app redeems it at POST /auth/handoff.
  createDesktopHandoff: () => api.post("/auth/desktop-handoff"),

  // helper: is user currently logged in?
  isAuthenticated: () => !!TokenStorage.get(),
};

export default authService;
